import { trackShare } from '../services/analyticsService';

/**
 * Where a share link can go: a row of platform links, plus the device's own
 * share sheet when it has one.
 *
 * Every link carries the same thing - the lander URL. None of these platforms
 * accept an image through a link; each one fetches `og:image` /
 * `twitter:image` off that URL, which is what the lander Function exists to
 * serve. So "sharing the image" means sharing the URL and letting the card
 * unfurl. (That also means the card has to be in real storage by the time
 * someone pastes - see BLOB_PROVIDER in api/CLAUDE.md.)
 *
 * The native sheet is an *addition* to the row rather than a replacement for
 * it: it's the only way to reach apps with no web share link at all
 * (Instagram, Messages, Snapchat), but a phone user should still get the
 * one-tap platform links too.
 *
 * Facebook ignores any text it's handed and builds the post from the page's
 * own meta tags, so its link carries the URL alone.
 */

const INTENTS = [
  {
    id: 'x',
    label: 'Share on X',
    icon: '/img/icons/twitter.png',
    href: ({ url, text }) => `https://x.com/intent/post?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`,
  },
  {
    id: 'facebook',
    label: 'Share on Facebook',
    icon: '/img/icons/facebook.png',
    href: ({ url }) => `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,
  },
  {
    id: 'whatsapp',
    label: 'Share on WhatsApp',
    icon: '/img/icons/whatsapp.png',
    href: ({ url, text }) => `https://wa.me/?text=${encodeURIComponent(`${text} ${url}`)}`,
  },
  {
    id: 'bluesky',
    label: 'Share on Bluesky',
    icon: '/img/icons/bluesky.png',
    href: ({ url, text }) => `https://bsky.app/intent/compose?text=${encodeURIComponent(`${text} ${url}`)}`,
  },
  {
    id: 'linkedin',
    label: 'Share on LinkedIn',
    icon: '/img/icons/linkedin.png',
    href: ({ url }) => `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`,
  },
];

// A gold disc with the platform's glyph filling its full diameter. 44px, the
// same height as Copy and More…, so the whole row lines up. The PNGs are
// black-on-transparent with their own padding, so filling the circle edge to
// edge still leaves the glyph a comfortable margin.
const iconClassName =
  'flex-shrink-0 w-11 h-11 rounded-full bg-nlg-gold overflow-hidden transition-transform duration-150 hover:scale-110 disabled:opacity-50 disabled:pointer-events-none';

// The native-sheet button keeps a text label - there's no single logo for
// "whichever apps you have" - and is styled to match SharePanel's Copy button
// exactly (same height, width floor and gold fill).
const textTargetClassName =
  'flex-shrink-0 inline-flex items-center justify-center h-11 min-w-[5.5rem] px-4 rounded-full bg-nlg-gold text-nlg-bg text-sm font-bold disabled:opacity-50';

function ShareTargets({ url, username }) {
  const text = `Come play a daily round of puzzles with ${username ? 'me' : 'us'}`;
  const canNativeShare = typeof navigator !== 'undefined' && typeof navigator.share === 'function';

  const onNativeShare = async () => {
    trackShare('native');
    try {
      await navigator.share({ title: 'Nice Light Games', text, url });
    } catch (err) {
      // Includes the user simply dismissing the sheet, which is not a failure
      // worth reporting.
      console.log('Native share dismissed or failed:', err);
    }
  };

  return (
    <>
      {INTENTS.map((intent) => {
        const image = <img src={intent.icon} alt="" className="w-full h-full block" />;
        return url ? (
          <a
            key={intent.id}
            href={intent.href({ url, text })}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => trackShare(intent.id)}
            aria-label={intent.label}
            title={intent.label}
            className={iconClassName}
          >
            {image}
          </a>
        ) : (
          <button key={intent.id} type="button" disabled aria-label={intent.label} className={iconClassName}>
            {image}
          </button>
        );
      })}

      {canNativeShare && (
        <button
          type="button"
          onClick={onNativeShare}
          disabled={!url}
          className={textTargetClassName}
        >
          More…
        </button>
      )}
    </>
  );
}

export default ShareTargets;
